import api from '../services/api';

// Notices API functions
export const getNotices = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page);
    if (params.pageSize) queryParams.append('pageSize', params.pageSize);
    if (params.filter) queryParams.append('filter', params.filter);
    if (params.status) queryParams.append('status', params.status);
    
    const url = `/notices${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching notices:', error);
    throw error;
  }
};

export const createNotice = async (noticeData) => {
  try {
    // Create FormData for multipart/form-data submission
    const formData = new FormData();
    
    // Append all form fields
    Object.keys(noticeData).forEach(key => {
      if (key === 'files') {
        // Handle file array
        if (noticeData.files && noticeData.files.length > 0) {
          noticeData.files.forEach(file => {
            formData.append('files[]', file);
          });
        }
      } else {
        formData.append(key, noticeData[key]);
      }
    });
    
    const response = await api.post('/notices', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error creating notice:', error);
    throw error;
  }
};

export const getNoticeById = async (id) => {
  try {
    const response = await api.get(`/notices/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching notice:', error);
    throw error;
  }
};

export const getNoticeForEdit = async (id) => {
  try {
    const response = await api.get(`/notices/edit/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching notice for edit:', error);
    throw error;
  }
};

export const updateNotice = async (noticeData) => {
  try {
    const response = await api.post('/notices/update', noticeData);
    return response.data;
  } catch (error) {
    console.error('Error updating notice:', error);
    throw error;
  }
};

export const deleteNotice = async (id, name = '') => {
  try {
    const response = await api.delete(`/notices/${id}?name=${encodeURIComponent(name)}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting notice:', error);
    throw error;
  }
};

export const getSuppliers = async () => {
  try {
    const response = await api.get('/notices/suppliers');
    return response.data;
  } catch (error) {
    console.error('Error fetching suppliers:', error);
    throw error;
  }
};

// Supplier Notice specific functions
export const getSupplierNoticeForCreate = async () => {
  try {
    const response = await api.get('/notices/create-notice');
    return response.data;
  } catch (error) {
    console.error('Error fetching supplier notice form data:', error);
    throw error;
  }
};

export const createSupplierNotice = async (noticeData) => {
  try {
    // Create FormData to support file uploads
    const formData = new FormData();
    formData.append('notice_title', noticeData.notice_title || '');
    formData.append('notice_text', noticeData.notice_text || '');
    formData.append('notice_shutdown', noticeData.notice_shutdown || '');
    
    // Add files if present
    if (noticeData.files && noticeData.files.length > 0) {
      for (let i = 0; i < noticeData.files.length; i++) {
        formData.append('files[]', noticeData.files[i]);
      }
    }
    
    const response = await api.post('/notices/save-notice', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error creating supplier notice:', error);
    throw error;
  }
};

export const getSupplierNoticeForEdit = async (id) => {
  try {
    const response = await api.get(`/notices/edit-notice/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching supplier notice for edit:', error);
    throw error;
  }
};

export const getSupplierNoticeForView = async (id) => {
  try {
    const response = await api.get(`/notices/view-notice/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching supplier notice for view:', error);
    throw error;
  }
};

export const updateSupplierNotice = async (noticeData) => {
  try {
    // Create FormData to support file uploads
    const formData = new FormData();
    formData.append('id', noticeData.id || noticeData.notice_id);
    formData.append('notice_title', noticeData.notice_title || '');
    formData.append('notice_text', noticeData.notice_text || '');
    formData.append('notice_status', noticeData.notice_status || 0);
    
    // Add files if present
    if (noticeData.files && noticeData.files.length > 0) {
      for (let i = 0; i < noticeData.files.length; i++) {
        formData.append('files[]', noticeData.files[i]);
      }
    }
    
    const response = await api.post('/notices/update-notice', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error updating supplier notice:', error);
    throw error;
  }
};

export const deleteSupplierNotice = async (id, name = '') => {
  try {
    const response = await api.delete(`/notices/${id}?name=${encodeURIComponent(name)}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting supplier notice:', error);
    throw error;
  }
};

// Remove a file from a supplier notice
export const removeSupplierNoticeFile = async (noticeId, fileId) => {
  try {
    const response = await api.delete(`/notices/remove-file/${noticeId}/${fileId}`);
    return response.data;
  } catch (error) {
    console.error('Error removing supplier notice file:', error);
    throw error;
  }
};

// Send notice email to suppliers
export const sendNoticeEmail = async (noticeId, sendData) => {
  try {
    // sendData should contain:
    // - specification_send_to: '0'=none, '1'=selected, '2'=group, '3'=project, '4'=all
    // - BP_Code: comma-separated supplier codes (for send_to='1')
    // - send_group: group code (for send_to='2')
    // - send_project: specification ID (for send_to='3')
    
    const formData = new FormData();
    formData.append('specification_send_to', sendData.specification_send_to || '0');
    
    if (sendData.specification_send_to === '1') {
      // Selected suppliers - send as comma-separated string
      formData.append('BP_Code', sendData.BP_Code || '');
    } else if (sendData.specification_send_to === '2') {
      // Group
      formData.append('send_group', sendData.send_group || '');
    } else if (sendData.specification_send_to === '3') {
      // Project
      formData.append('send_project', sendData.send_project || '');
    }
    
    const response = await api.post(`/notices/send-mail/${noticeId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error sending notice email:', error);
    throw error;
  }
};

// Helper function to format notice data for display
export const formatNoticeData = (notice) => {
  return {
    ...notice,
    formattedDate: notice.created_at ? new Date(notice.created_at).toLocaleDateString() : '',
    areasText: notice.areas ? notice.areas.map(area => area.area_name).join(', ') : ''
  };
};
